import React, { FC, useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Modal from 'components/commercetools-ui/organisms/modal';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { desktop, tablet } from 'helpers/utils/screensizes';
import useTranslation from 'providers/i18n/hooks/useTranslation';
import Image, { ImageProps } from '../image';
import { usePinchZoom } from './hooks/usePinchZoom';
import { usePreloadAdjacentImages } from './hooks/usePreloadAdjacentImages';
import type { SlideImageData } from './hooks/useSliderModal';
import { useSwipeGesture } from './hooks/useSwipeGesture';

export interface SliderModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string | null;
  imageAlt: string | null;
  imageMedia?: ImageProps['media'];
  /** Full array of images for gallery navigation */
  images?: SlideImageData[];
  /** Current active image index */
  activeIndex?: number;
  /** Total number of images */
  totalImages?: number;
  /** Navigate to specific image by index */
  onNavigate?: (index: number) => void;
  /** Navigate to next image */
  onNext?: () => void;
  /** Navigate to previous image */
  onPrevious?: () => void;
  /** Can navigate to next image */
  canGoNext?: boolean;
  /** Can navigate to previous image */
  canGoPrevious?: boolean;
  /** Optional callback when image fails to load (for analytics/error tracking) */
  onImageError?: (imageUrl: string | null) => void;
}

function getModalStyles(isMobile: boolean) {
  return {
    content: {
      padding: 0,
      overflow: 'hidden',
      width: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : '90vh',
      maxWidth: '100%',
      maxHeight: '100%',
      backgroundColor: 'transparent',
      border: 'none',
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    },
  };
}

// Thumbnail dimensions (mobile: 80px, desktop: 112px) - larger mobile size for touch accessibility
const THUMBNAIL_SIZE_MOBILE = 80;
const THUMBNAIL_SIZE_DESKTOP = 112;

const SliderModal: FC<SliderModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  imageAlt,
  imageMedia,
  images = [],
  activeIndex = 0,
  totalImages = 0,
  onNavigate,
  onNext,
  onPrevious,
  canGoNext = false,
  canGoPrevious = false,
  onImageError,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const { translate } = useTranslation();
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTabletOrLarger] = useMediaQuery(tablet);
  const { hasTouchCapability } = useTouchDevice();
  const isMobile = !isTabletOrLarger;
  const modalStyles = getModalStyles(isMobile);

  // Track current image to prevent stale callbacks from updating state for wrong image.
  // When user rapidly opens/closes modal or switches images, the onLoad/onError from
  // a previous image could fire after a new image started loading.
  const currentImageRef = useRef<string | null>(null);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Determine if we should show navigation UI (only for multiple images)
  const showNavigation = totalImages > 1;

  // Preload adjacent images for smoother navigation
  usePreloadAdjacentImages(images, activeIndex, isOpen);

  // Pinch-to-zoom on touch devices
  const { setContainerRef: setZoomRef, scale, translateX: zoomTx, translateY: zoomTy, isZoomed, resetZoom, animating } = usePinchZoom({ enabled: isOpen && hasTouchCapability });

  // Reset zoom when modal opens or image changes
  useEffect(() => {
    if (isOpen) {
      resetZoom();
    }
  }, [isOpen, activeIndex, resetZoom]);

  // Swipe gestures for touch navigation — disabled when zoomed so drag pans instead
  const swipeRef = useSwipeGesture<HTMLDivElement>({
    onSwipeLeft: onNext,
    onSwipeRight: onPrevious,
    enabled: isOpen && showNavigation && !isZoomed,
  });

  // Merge swipe + zoom refs into a single stable callback ref
  const combinedRef = useCallback(
    (el: HTMLDivElement | null) => {
      (swipeRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      setZoomRef(el);
    },
    [setZoomRef],
  );

  // Reset loading/error state when image changes or modal opens.
  // No cleanup needed - ref is overwritten on each open, and the callbacks
  // check against current props anyway, making stale callbacks harmless.
  useEffect(() => {
    const imageKey = imageMedia?.mediaId || imageUrl || null;

    if (isOpen && imageKey) {
      currentImageRef.current = imageKey;
      setIsLoading(true);
      setHasError(false);
      setImageDimensions(null);
    }
  }, [isOpen, imageUrl, imageMedia?.mediaId]);

  // Keyboard navigation (left/up for previous, right/down for next)
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if focus is on an interactive element (button, input, etc.)
      // unless it's a gallery thumbnail (identified by data attribute)
      const activeEl = document.activeElement;
      const isInteractiveElement =
        activeEl?.tagName === 'BUTTON' ||
        activeEl?.tagName === 'INPUT' ||
        activeEl?.getAttribute('role') === 'button';
      const isThumbnail = activeEl?.hasAttribute('data-gallery-thumbnail');

      if (isInteractiveElement && !isThumbnail) return;

      if ((e.key === 'ArrowLeft' || e.key === 'ArrowUp') && canGoPrevious && onPrevious) {
        e.preventDefault();
        onPrevious();
      }
      if ((e.key === 'ArrowRight' || e.key === 'ArrowDown') && canGoNext && onNext) {
        e.preventDefault();
        onNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, canGoPrevious, canGoNext, onPrevious, onNext]);

  // Clean up thumbnail refs when images array shrinks to prevent stale references
  useEffect(() => {
    thumbnailRefs.current = thumbnailRefs.current.slice(0, images.length);
  }, [images.length]);

  // Auto-scroll thumbnails to keep active one visible
  useEffect(() => {
    if (!showNavigation) return;

    const container = thumbnailContainerRef.current;
    const activeThumbnail = thumbnailRefs.current[activeIndex];

    if (container && activeThumbnail) {
      const containerRect = container.getBoundingClientRect();
      const thumbnailRect = activeThumbnail.getBoundingClientRect();

      const containerLeft = containerRect.left;
      const containerRight = containerRect.right;
      const thumbnailLeft = thumbnailRect.left;
      const thumbnailRight = thumbnailRect.right;

      if (thumbnailLeft < containerLeft || thumbnailRight > containerRight) {
        const scrollAmount =
          thumbnailLeft - containerLeft + container.scrollLeft - containerRect.width / 2 + thumbnailRect.width / 2;

        container.scrollTo({
          left: scrollAmount,
          behavior: 'smooth',
        });
      }
    }
  }, [activeIndex, showNavigation]);

  const setThumbnailRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      thumbnailRefs.current[index] = el;
    },
    [],
  );

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    // Only update state if this callback is for the current image
    const imageKey = imageMedia?.mediaId || imageUrl || null;
    if (imageKey === currentImageRef.current) {
      setIsLoading(false);
      const img = e.currentTarget;
      if (img.naturalWidth && img.naturalHeight) {
        setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      }
    }
  };

  const handleImageError = () => {
    // Only update state if this callback is for the current image
    const imageKey = imageMedia?.mediaId || imageUrl || null;
    if (imageKey === currentImageRef.current) {
      setIsLoading(false);
      setHasError(true);
      setImageDimensions(null);
      onImageError?.(imageUrl);
    }
  };

  // Close when clicking the dark backdrop area, but not when clicking the visible image pixels.
  // objectFit: scale-down centres the image with letterboxing — simulate those bounds to decide.
  // naturalWidth/naturalHeight are density-corrected CSS pixels (per spec), same unit as
  // getBoundingClientRect(), so no DPR conversion is needed.
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isZoomed) return;

      if (imageDimensions) {
        const container = e.currentTarget.getBoundingClientRect();
        const { width: natW, height: natH } = imageDimensions;
        const containerRatio = container.width / container.height;
        const imageRatio = natW / natH;

        let renderedW: number;
        let renderedH: number;
        if (natW <= container.width && natH <= container.height) {
          // Image is smaller than the container — renders at its natural size
          renderedW = natW;
          renderedH = natH;
        } else if (imageRatio > containerRatio) {
          renderedW = container.width;
          renderedH = container.width / imageRatio;
        } else {
          renderedH = container.height;
          renderedW = container.height * imageRatio;
        }

        const imgLeft = container.left + (container.width - renderedW) / 2;
        const imgTop = container.top + (container.height - renderedH) / 2;

        if (
          e.clientX >= imgLeft &&
          e.clientX <= imgLeft + renderedW &&
          e.clientY >= imgTop &&
          e.clientY <= imgTop + renderedH
        ) {
          return;
        }
      }

      onClose();
    },
    [isZoomed, imageDimensions, onClose],
  );

  const hasValidImage = imageUrl || imageMedia?.mediaId;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyles}
      closeButton
      preventScroll
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      contentLabel={imageAlt || translate('common.enlarged-image-view', { defaultMessage: 'Enlarged image view' })}
      className="text-white focus:outline-none lg:text-black"
    >
      <div className="relative flex size-full flex-col">
        {/* Main image area with navigation arrows */}
        {/* touch-action: none on touch devices so pinch/pan aren't intercepted by browser */}
        <div
          ref={combinedRef}
          data-testid="slider-modal-swipe-area"
          className="relative flex min-h-0 flex-1 cursor-pointer items-center justify-center overflow-hidden"
          style={{ touchAction: hasTouchCapability ? 'none' : 'pan-y' }}
          onClick={handleBackdropClick}
        >
          {/* Previous arrow */}
          {showNavigation && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onPrevious?.(); }}
              disabled={!canGoPrevious}
              aria-label={translate('common.previous-image', { defaultMessage: 'Previous image' })}
              className={`absolute left-16 top-1/2 z-10 flex size-44 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 transition-opacity md:left-24 md:size-48 ${
                canGoPrevious ? 'cursor-pointer opacity-100 hover:bg-black/70' : 'cursor-not-allowed opacity-30'
              }`}
            >
              <ChevronLeftIcon className="size-24 text-white md:size-32" />
            </button>
          )}

          {/* Loading spinner */}
          {isLoading && hasValidImage && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="size-48 animate-spin rounded-full border-4 border-white/30 border-t-white" />
            </div>
          )}

          {/* Error state */}
          {hasError && (
            <div className="flex flex-col items-center justify-center gap-16 text-white">
              <svg className="size-64 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p className="text-lg opacity-75">
                {translate('common.image-load-error', { defaultMessage: 'Unable to load image' })}
              </p>
            </div>
          )}

          {/* Image - scale-down prevents upscaling small images while still fitting large ones */}
          {hasValidImage && !hasError && (
            <div
              className="relative size-full cursor-default"
              style={{
                transform: `translate(${zoomTx}px, ${zoomTy}px) scale(${scale})`,
                transformOrigin: 'center center',
                transition: animating ? 'transform 200ms ease-out' : undefined,
                willChange: isZoomed ? 'transform' : undefined,
              }}
            >
              <Image
                key={imageMedia?.mediaId || imageUrl}
                src={imageUrl || ''}
                alt={imageAlt || translate('common.enlarged-view', { defaultMessage: 'Enlarged view' })}
                media={imageMedia}
                cloudinaryPreset="modal"
                loading="eager"
                style={{ objectFit: 'scale-down' }}
                className={`transition-opacity duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                fill
                sizes={isMobile ? '100vw' : '90vw'}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            </div>
          )}

          {/* Next arrow */}
          {showNavigation && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onNext?.(); }}
              disabled={!canGoNext}
              aria-label={translate('common.next-image', { defaultMessage: 'Next image' })}
              className={`absolute right-16 top-1/2 z-10 flex size-44 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 transition-opacity md:right-24 md:size-48 ${
                canGoNext ? 'cursor-pointer opacity-100 hover:bg-black/70' : 'cursor-not-allowed opacity-30'
              }`}
            >
              <ChevronRightIcon className="size-24 text-white md:size-32" />
            </button>
          )}
        </div>

        {/* Image counter and thumbnails */}
        {showNavigation && (
          <div className="mt-4 flex shrink-0 flex-col items-center gap-3">
            {/* Counter - aria-live announces image changes to screen readers */}
            <p className="text-sm text-white/80" aria-live="polite" aria-atomic="true">
              {activeIndex + 1} / {totalImages}
            </p>

            {/* Thumbnail strip */}
            <div
              ref={thumbnailContainerRef}
              className="max-w-full overflow-x-auto scroll-smooth px-4 pb-2 scrollbar-hide"
            >
              <div className="flex min-w-min gap-x-2 md:gap-x-3">
                {images.map((image, index) => {
                  const isActive = index === activeIndex;
                  return (
                    <div
                      key={image.media?.mediaId || image.src || index}
                      ref={setThumbnailRef(index)}
                      role="button"
                      tabIndex={0}
                      data-gallery-thumbnail
                      aria-label={translate('common.view-image-n-of-total', {
                        defaultMessage: 'View image {current} of {total}',
                        values: { current: index + 1, total: totalImages },
                      })}
                      aria-current={isActive ? 'true' : undefined}
                      className={`relative size-64 shrink-0 cursor-pointer border-2 transition-colors md:size-80 ${
                        isActive ? 'border-white' : 'border-white/30'
                      }`}
                      onClick={() => onNavigate?.(index)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onNavigate?.(index);
                        }
                      }}
                    >
                      <div className="relative size-full">
                        <Image
                          src={image.src || ''}
                          alt={image.alt || ''}
                          media={image.media}
                          cloudinaryPreset="thumbnail"
                          style={{ objectFit: 'contain' }}
                          width={isDesktopSize ? THUMBNAIL_SIZE_DESKTOP : THUMBNAIL_SIZE_MOBILE}
                          height={isDesktopSize ? THUMBNAIL_SIZE_DESKTOP : THUMBNAIL_SIZE_MOBILE}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SliderModal;
